import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Box, Typography, Button, Chip, TextField, useTheme, useMediaQuery, FormControlLabel, Checkbox, Link } from '@mui/material';
const supabase = require('../../misc/supabasestuff').default;
const baseUrl = process.env.REACT_APP_BASE_URL;

function SummaryContent({ subscription }) {
    const renderChipSection = (title, items, prefix = '', defaultText = '') => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {items && items.length > 0 ? (
                    items.map((item) => (
                        <Chip
                            key={typeof item === 'object' ? item.id : item}
                            label={typeof item === 'object' ? item.label : `${prefix}${item}`}
                            size="small"
                        />
                    ))
                ) : (
                    <Chip label={defaultText} size="small" />
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {renderChipSection('Områden', subscription.omraden, '', 'Alla områden')}
            {renderChipSection('Antal rum', subscription.rooms, '', 'Alla antal rum')}
            {renderChipSection('Bostadstyper', subscription.bostadstyp, '', 'Alla typer')}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Yta och Hyra
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label={subscription.minyta > 0 ? `Min ${subscription.minyta} m²` : 'Alla ytor'} size="small" />
                    <Chip label={subscription.maxhyra > 0 ? `Max ${subscription.maxhyra} kr` : 'Alla hyror'} size="small" />
                </Box>
            </Box>
        </>
    );
}

export default function SummarySlider({ subscription, onCreateSubscription, historicalApartmentCount, isLoggedIn, userEmail, openSummaryDialog, setOpenSummaryDialog }) {
    const [email, setEmail] = useState(userEmail || '');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const historicalApartmentStartDate = new Date('2021-01-01');
    const today = new Date();
    const daysOfHistoricalData = Math.floor((today - historicalApartmentStartDate) / (1000 * 60 * 60 * 24));
    const yearlyMatchingApartments = Math.floor(historicalApartmentCount / daysOfHistoricalData * 365);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!termsAccepted) {
            alert("Du måste acceptera villkoren för att fortsätta.");
            return;
        }
        setIsLoading(true);

        try {
            if (isLoggedIn) {
                // Logged-in user: create subscription directly
                const { data: { session } } = await supabase.auth.getSession();
                const response = await fetch(`${baseUrl}/bovaka`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`
                    },
                    body: JSON.stringify(subscription),
                });

                if (!response.ok) {
                    throw new Error('Failed to create subscription');
                }

                navigate('/bevakningar');
            } else {
                // Not logged in: call /signup endpoint
                const response = await fetch(`${baseUrl}/signup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: email,
                        subscription: subscription,
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to sign up');
                }

                // Show "check your email" popup
                setOpenPopup(true);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Det uppstod ett fel. Vänligen försök igen.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClosePopup = () => {
        setOpenPopup(false);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2,
            pb: isMobile ? 8 : 2
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
            }}>
                <Typography variant="h6" color="text.secondary">
                    Din bevakning matchar ca.
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} color="primary" fontWeight="bold">
                    {yearlyMatchingApartments} lägenheter per år
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                {!isLoggedIn && (
                    <TextField
                        fullWidth
                        label="E-postadress"
                        type="email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        required
                        disabled={isLoading}
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            name="termsAccepted"
                            color="primary"
                            disabled={isLoading}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Jag accepterar <Link href="/policy" target="_blank">villkoren</Link>
                        </Typography>
                    }
                />
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    disabled={!email || !termsAccepted || isLoading}
                    sx={{ mt: 2, height: 48, mb: 2 }}
                >
                    {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Skapa Bevakning'
                    )}
                </Button>
            </form>

            {isSmallScreen ? (
                <>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setOpenSummaryDialog(true)}
                        sx={{ mb: 2 }}
                    >
                        Visa sammanfattning av bevakning
                    </Button>
                    <Dialog
                        open={openSummaryDialog}
                        onClose={() => setOpenSummaryDialog(false)}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle>Sammanfattning av din bevakning</DialogTitle>
                        <DialogContent>
                            <SummaryContent subscription={subscription} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenSummaryDialog(false)}>Stäng</Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>Sammanfattning av din bevakning</Typography>
                    <SummaryContent subscription={subscription} />
                </Box>
            )}

            {/* Popup Dialog for active subscription */}
            <Dialog open={openPopup} onClose={handleClosePopup}>
                <DialogTitle>Kolla din e-post!</DialogTitle>
                <DialogContent>
                    <Typography>
                        Vi har skickat en inloggningslänk till din e-post. Klicka på länken i mailet för att logga in och se dina bevakningar.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePopup} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
